import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronDown, ChevronUp, Info, Loader2, Search, Settings2, Star, Ticket,
  Trophy,
} from 'lucide-react';
import client from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import TeamBadge from '../../components/TeamBadge';
import { useSportsbook } from '../../pages/sports/useSportsbook';
import SportsUnavailable from '../../pages/sports/SportsUnavailable';
import { getLiveClock } from '../../pages/sports/liveClock';
import BetSlip from '../../pages/sports/BetSlip';
import './herkulSports.css';

// Herkül şemasının spor sayfası.
//
// **Veri ve kupon mantığı çatallanmaz**: sayfa `useSportsbook()` hook'unu
// olduğu gibi tüketir (oran kaynakları, seçim doğrulaması, freebet, kupon
// gönderimi orada durur). Burada yalnızca referans arayüzün yerleşimi vardır:
// spor listesi rayı, üç sütunlu gövde ve sağdaki araç panelleri.
//
// Proje kuralı iç sayfaların şema başına çatallanmamasıdır (bkz. CLAUDE.md);
// bu sayfa bilinçli bir istisnadır — referans sportsbook'un düzeni ortak
// sayfanınkinden tümüyle farklı ve yalnızca bu şema için istendi.

// Spor kodu büyük harflidir (SOCCER, ICE_HOCKEY…); ortak spor sayfasıyla
// aynı anahtarlar kullanılır, ayrışırlarsa bir sayfada ikon çıkar diğerinde
// çıkmaz.
const SPORT_ICONS = {
  SOCCER: '⚽', BASKETBALL: '🏀', TENNIS: '🎾', VOLLEYBALL: '🏐',
  BASEBALL: '⚾', ICE_HOCKEY: '🏒', TABLE_TENNIS: '🏓', HANDBALL: '🤾',
  AMERICAN_FOOTBALL: '🏈', BOXING: '🥊', MMA: '🥊', CRICKET: '🏏',
  GOLF: '⛳', DARTS: '🎯', RUGBY_LEAGUE: '🏉', RUGBY_UNION: '🏉',
  AUSTRALIAN_RULES_FOOTBALL: '🏉', LACROSSE: '🥍', SNOOKER: '🎱',
};

const sportIcon = (sport) => SPORT_ICONS[sport] || '🏆';

const money = (value) => Number(value || 0).toLocaleString('tr-TR', {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
});

const isToday = (iso) => {
  if (!iso) return false;
  try { return new Date(iso).toDateString() === new Date().toDateString(); } catch { return false; }
};

const formatKickoff = (iso) => {
  if (!iso) return { day: '', time: '' };
  try {
    const date = new Date(iso);
    return {
      day: date.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit' }),
      time: date.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }),
    };
  } catch { return { day: '', time: '' }; }
};

// Referans tablosunda iki sütun grubu vardır: "Sonuç" (maç sonucu) ve
// "Toplam" (alt/üst). İkincisi bulunamazsa hücreler yer tutar — sütun
// başlıkları maçtan maça kaymamalıdır.
const TOTALS_KEYS = ['totals', 'team_totals'];

// Maç sonucu hücreleri **sabit yuvalara** oturur: 1 · X · 2. Seçimleri
// geldikleri sırayla dizmek iki sorun üretiyordu — beraberliği olmayan
// dallarda (beyzbol, tenis) deplasman ortadaki yuvaya kayıyor ve oyuncu
// yanlış hücreye basıyordu; ayrıca sağlayıcı etiketi tam takım adı olduğu
// için buton taşıp yanındaki sütunun üstüne biniyordu.
const RESULT_SLOTS = ['1', 'X', '2'];

const resultSlot = (selection) => {
  const key = String(selection?.pick_key || '');
  if (key === 'DRAW') return 1;
  if (key.startsWith('WIN:HOME')) return 0;
  if (key.startsWith('WIN:AWAY')) return 2;
  return -1;
};

// Sonuç sütununda kısa etiket kullanılır; tam ad butona title olarak kalır.
const shortLabel = (selection, slot) => (
  slot >= 0 ? RESULT_SLOTS[slot] : selection.label
);

const OddCell = ({ selection, active, label, onSelect }) => {
  if (!selection) return <span className="hkb-odd is-empty">—</span>;
  return (
    <button
      type="button"
      className={`hkb-odd ${active ? 'is-active' : ''} ${selection.custom ? 'is-custom' : ''}`}
      onClick={() => onSelect(selection)}
      title={selection.label}
    >
      <span className="hkb-odd-label">{label ?? selection.label}</span>
      <span className="hkb-odd-val">
        {selection.custom && <Star size={9} fill="currentColor" />}
        {Number(selection.odd).toFixed(2)}
      </span>
    </button>
  );
};

const MatchRow = ({
  event, activePick, onSelect, favourite, onToggleFavourite, onLoadMarkets,
}) => {
  const [open, setOpen] = useState(false);
  const [extraMarkets, setExtraMarkets] = useState(null);
  const [extraLoading, setExtraLoading] = useState(false);

  const markets = useMemo(() => {
    const base = event.markets || [];
    if (!extraMarkets) return base;
    // Ayrıntı çağrısı aynı marketin daha geniş hâlini döndürebilir; anahtara
    // göre birleştirilir ki satırdaki oran ile açılan alandaki çelişmesin.
    const byKey = new Map(extraMarkets.map((market) => [market.market_key, market]));
    const merged = base.map((market) => byKey.get(market.market_key) || market);
    extraMarkets.forEach((market) => {
      if (!merged.some((item) => item.market_key === market.market_key)) merged.push(market);
    });
    return merged;
  }, [event.markets, extraMarkets]);

  const primary = markets[0];
  const totals = markets.find((market) => TOTALS_KEYS.includes(market.market_key));
  const kickoff = formatKickoff(event.start_time);
  const clock = event.live ? getLiveClock(event) : null;

  // Satırda çizilmeyen marketler açılır alana iner.
  const rest = markets.filter(
    (market) => market !== primary && market !== totals,
  );

  // Ek oranlar yalnızca satır açıldığında istenir: her satır için önden
  // çağrı atmak liste başına onlarca istek üretirdi.
  useEffect(() => {
    if (!open || extraMarkets !== null || !onLoadMarkets) return undefined;
    let alive = true;
    setExtraLoading(true);
    Promise.resolve(onLoadMarkets(event))
      .then((rows) => { if (alive) setExtraMarkets(rows || []); })
      .catch(() => { if (alive) setExtraMarkets([]); })
      .finally(() => { if (alive) setExtraLoading(false); });
    return () => { alive = false; };
  }, [open, extraMarkets, event, onLoadMarkets]);

  const isActive = (selection) => (
    selection && activePick
    && activePick.match_id === event.event_key
    && activePick.pick_key === selection.pick_key
  );

  const pick = (market) => (selection) => onSelect(event, market, selection);

  // Maç sonucu seçimleri sabit 1/X/2 yuvalarına dağıtılır; karşılığı olmayan
  // yuva boş kalır (beraberliği olmayan dallarda X gibi).
  const resultCells = [null, null, null];
  (primary?.selections || []).forEach((selection) => {
    const slot = resultSlot(selection);
    if (slot >= 0 && !resultCells[slot]) resultCells[slot] = selection;
  });
  // Yuvaya oturmayan seçimler (tanınmayan pick_key) sıradaki boş yuvaya
  // konur — oran hiç çizilmemesindense yerinde çizilmesi yeğdir.
  (primary?.selections || []).forEach((selection) => {
    if (resultCells.includes(selection)) return;
    const free = resultCells.indexOf(null);
    if (free >= 0) resultCells[free] = selection;
  });

  const totalCells = (totals?.selections || []).slice(0, 2);
  const canExpand = rest.length > 0 || event.feed === 'oddsapi';

  return (
    <>
      <div className={`hkb-row ${open ? 'is-open' : ''}`}>
        <div className="hkb-when">
          {event.live ? (
            <>
              <strong>{clock?.label}</strong>
              <span className="hkb-live">CANLI</span>
            </>
          ) : (
            <>
              <strong>{kickoff.day}</strong>
              <span>{kickoff.time}</span>
            </>
          )}
        </div>

        <div className="hkb-teams">
          <span className="hkb-team">
            <TeamBadge name={event.home} logo={event.home_logo} size={17} />
            <span>{event.home}</span>
          </span>
          <span className="hkb-team">
            <TeamBadge name={event.away} logo={event.away_logo} size={17} />
            <span>{event.away}</span>
          </span>
        </div>

        <div className="hkb-group">
          {resultCells.map((selection, index) => (
            <OddCell
              // Yuva sırası sabittir; anahtar da yuvadır.
              key={RESULT_SLOTS[index]}
              selection={selection}
              label={selection ? shortLabel(selection, resultSlot(selection)) : null}
              active={isActive(selection)}
              onSelect={pick(primary)}
            />
          ))}
        </div>

        <div className="hkb-group is-totals">
          {[0, 1].map((index) => (
            <OddCell
              key={index}
              selection={totalCells[index] || null}
              active={isActive(totalCells[index])}
              onSelect={pick(totals)}
            />
          ))}
        </div>

        <div className="hkb-row-tools">
          {canExpand && (
            <button
              type="button"
              className={`hkb-expand ${open ? 'is-open' : ''}`}
              onClick={() => setOpen((value) => !value)}
              aria-expanded={open}
              aria-label="Diğer oranlar"
            >
              <ChevronDown size={14} />
            </button>
          )}
          <button
            type="button"
            className={`hkb-fav ${favourite ? 'is-on' : ''}`}
            onClick={() => onToggleFavourite(event.event_key)}
            aria-label="Favorilere ekle"
            aria-pressed={favourite}
          >
            <Star size={13} fill={favourite ? 'currentColor' : 'none'} />
          </button>
        </div>
      </div>

      {open && (
        <div className="hkb-extra">
          {extraLoading && (
            <div className="hkb-empty"><Loader2 size={15} className="herkul-spin" /></div>
          )}
          {!extraLoading && rest.length === 0 && (
            <p className="hkb-empty">Bu maç için başka market yok.</p>
          )}
          {rest.map((market) => (
            <div className="hkb-market" key={market.market_key}>
              <p className="hkb-market-name">{market.label || market.market_key}</p>
              <div className="hkb-market-odds">
                {(market.selections || []).map((selection) => (
                  <OddCell
                    key={selection.pick_key}
                    selection={selection}
                    active={isActive(selection)}
                    onSelect={pick(market)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

const PAGE_SIZE = 12;

const HerkulSportsPage = () => {
  const sb = useSportsbook();
  const { user } = useAuth();

  const [view, setView] = useState('home');
  const [shown, setShown] = useState(PAGE_SIZE);
  const [favourites, setFavourites] = useState(() => {
    try { return JSON.parse(localStorage.getItem('herkul:sb-fav') || '[]'); } catch { return []; }
  });

  // Sayfa açıkken kabuğun dar genişlik sınırı kalkar (ortak sportsbook ile
  // aynı mekanizma, bkz. sports.css `body.sb-page`).
  useEffect(() => {
    document.body.classList.add('sb-page');
    return () => document.body.classList.remove('sb-page');
  }, []);

  const toggleFavourite = useCallback((id) => {
    setFavourites((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      try { localStorage.setItem('herkul:sb-fav', JSON.stringify(next)); } catch { /* yoksay */ }
      return next;
    });
  }, []);

  const events = useMemo(() => {
    const list = sb.bookList || [];
    if (view === 'live') return list.filter((event) => event.live);
    return list;
  }, [sb.bookList, view]);

  useEffect(() => { setShown(PAGE_SIZE); }, [view, sb.bookSport, sb.bookCompetition]);

  const activePick = sb.coupon[sb.coupon.length - 1] || null;

  const selectPick = useCallback((event, market, selection) => {
    if (!market || !selection) return;
    sb.addBookPick(event, market, selection);
  }, [sb]);

  if (sb.unavailable) return <SportsUnavailable />;

  return (
    <div className="hkb">
      {/* Üst ince gezinme — referansta "Ana Sayfa | Canlı | Sonuçlar". */}
      <div className="hkb-subnav">
        <div className="hkb-subnav-tabs">
          {[
            { key: 'home', label: 'Ana Sayfa' },
            { key: 'live', label: 'Canlı' },
            { key: 'results', label: 'Sonuçlar' },
          ].map((item) => (
            <button
              key={item.key}
              type="button"
              className={`hkb-subnav-tab ${view === item.key ? 'is-active' : ''}`}
              onClick={() => setView(item.key)}
            >
              {item.label}
            </button>
          ))}
        </div>
        <div className="hkb-subnav-tools">
          <span className="hkb-tool"><Search size={15} /></span>
          <span className="hkb-tool"><Settings2 size={15} /></span>
        </div>
      </div>

      <div className="hkb-body">
        <SportsRail sb={sb} />

        <main className="hkb-main">
          {view === 'results' ? (
            <ResultsPanel user={user} />
          ) : (
            <>
              <TopWins />

              <section className="hkb-block">
                <div className="hkb-block-head">
                  <h2>{view === 'live' ? 'CANLI MAÇLAR' : 'POPÜLER MAÇLAR'}</h2>
                  <div className="hkb-chips">
                    {Object.keys(sb.bookSports || {}).slice(0, 6).map((sport) => (
                      <button
                        key={sport}
                        type="button"
                        className={`hkb-chip ${sb.bookSport === sport ? 'is-active' : ''}`}
                        onClick={() => sb.selectBookSport(
                          sb.bookSport === sport ? '' : sport,
                        )}
                        title={sport}
                      >
                        {sportIcon(sport)}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="hkb-table">
                  <div className="hkb-table-head">
                    <span>Maçlar</span>
                    <span className="hkb-th-group">Sonuç</span>
                    <span className="hkb-th-group is-totals">Toplam</span>
                    <span />
                  </div>

                  {sb.bookLoading && events.length === 0 && (
                    <div className="hkb-empty"><Loader2 size={16} className="herkul-spin" /></div>
                  )}
                  {!sb.bookLoading && events.length === 0 && (
                    <p className="hkb-empty">
                      {view === 'live'
                        ? 'Şu anda canlı maç yok.'
                        : 'Gösterilecek maç bulunamadı.'}
                    </p>
                  )}

                  {events.slice(0, shown).map((event) => (
                    <MatchRow
                      key={event.event_key}
                      event={event}
                      activePick={activePick}
                      onSelect={selectPick}
                      favourite={favourites.includes(event.event_key)}
                      onToggleFavourite={toggleFavourite}
                      onLoadMarkets={sb.loadBookEventMarkets}
                    />
                  ))}
                </div>

                {events.length > shown && (
                  <button
                    type="button"
                    className="hkb-more"
                    onClick={() => setShown((count) => count + PAGE_SIZE)}
                  >
                    DAHA FAZLASINI GÖSTER
                    <ChevronDown size={15} />
                  </button>
                )}
              </section>
            </>
          )}
        </main>

        <aside className="hkb-side">
          <BetChecker />
          <BetBuilder sb={sb} events={sb.bookList || []} />
        </aside>
      </div>

      <BetSheet sb={sb} />
    </div>
  );
};

// ---------------------------- alttan açılan kupon ------------------------ //

// Kupon sağ sütunda değil **sayfanın altındadır**: seçim yapılırken göz
// listede kalır, kupon ise ekranın altından açılır. Kapalıyken yalnızca özet
// şerit görünür (seçim sayısı, toplam oran, muhtemel kazanç) — kupona dönüş
// yolu her zaman ortadadır.
//
// İçerik ortak `BetSlip` bileşenidir; burada yalnızca açılır kabuk vardır.
const BetSheet = ({ sb }) => {
  const [open, setOpen] = useState(false);
  const count = sb.coupon.length;

  // Kupon boşalınca panel kendiliğinden kapanır; boş bir panel ekranın
  // yarısını kaplıyordu.
  useEffect(() => { if (!count) setOpen(false); }, [count]);

  if (!count) return null;

  return (
    <div className={`hkb-sheet ${open ? 'is-open' : ''}`}>
      <button
        type="button"
        className="hkb-sheet-bar"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
      >
        <span className="hkb-sheet-badge">
          <Ticket size={15} />
          {count}
        </span>
        <span className="hkb-sheet-stat">
          <small>Toplam Oran</small>
          <b>{Number(sb.totalOdd || 0).toFixed(2)}</b>
        </span>
        <span className="hkb-sheet-stat">
          <small>Muhtemel Kazanç</small>
          <b>{money(sb.winPossible)} ₺</b>
        </span>
        <ChevronUp size={17} className="hkb-sheet-caret" />
      </button>

      {open && (
        <div className="hkb-sheet-body">
          <BetSlip
            coupon={sb.coupon}
            removePick={sb.removePick}
            clearCoupon={sb.clearCoupon}
            stake={sb.stake}
            setStake={sb.setStake}
            totalOdd={sb.totalOdd}
            winPossible={sb.winPossible}
            effectiveStake={sb.effectiveStake}
            freebets={sb.freebets}
            freebetId={sb.freebetId}
            setFreebetId={sb.setFreebetId}
            activeFreebet={sb.activeFreebet}
            freebetOddShort={sb.freebetOddShort}
            placeBet={sb.placeBet}
            submitting={sb.submitting}
            onClose={() => setOpen(false)}
          />
        </div>
      )}
    </div>
  );
};

// --------------------------- spor listesi rayı --------------------------- //

const SportsRail = ({ sb }) => {
  const sports = Object.entries(sb.bookSports || {});
  return (
    <aside className="hkb-rail">
      <div className="hkb-rail-head">
        <Trophy size={15} />
        Popüler Etkinlikler
      </div>
      <button
        type="button"
        className={`hkb-sport ${!sb.bookSport ? 'is-active' : ''}`}
        onClick={sb.clearBookFilters}
      >
        <span className="hkb-sport-icon">🏆</span>
        <span className="hkb-sport-name">Tümü</span>
        <span className="hkb-sport-count">{sb.bookEventCounts?.total ?? 0}</span>
      </button>
      {sports.map(([sport, comps]) => (
        <button
          key={sport}
          type="button"
          className={`hkb-sport ${sb.bookSport === sport ? 'is-active' : ''}`}
          onClick={() => sb.selectBookSport(sport)}
        >
          <span className="hkb-sport-icon">{sportIcon(sport)}</span>
          <span className="hkb-sport-name">{comps[0]?.sport_label || sport}</span>
          <span className="hkb-sport-count">{sb.bookEventCounts?.sports?.[sport] || 0}</span>
        </button>
      ))}
      {sports.length === 0 && (
        <p className="hkb-rail-note">Oran kaynağı kapalı ya da liste yükleniyor.</p>
      )}
    </aside>
  );
};

// ------------------------- En Çok Kazananlar şeridi ---------------------- //

const TopWins = () => {
  const [items, setItems] = useState([]);

  useEffect(() => {
    let alive = true;
    client.get('/sports/top-wins', { params: { limit: 12 } })
      .then(({ data }) => { if (alive) setItems(data?.items || []); })
      .catch(() => { /* şerit yan bir vitrin öğesidir */ });
    return () => { alive = false; };
  }, []);

  // Hiç kazanan kupon yoksa şerit çizilmez: boş bir "En Çok Kazananlar"
  // başlığı siteyi terk edilmiş gösterir.
  if (!items.length) return null;

  return (
    <section className="hkb-block">
      <div className="hkb-block-head"><h2>En Çok Kazananlar</h2></div>
      <div className="hkb-wins">
        {items.map((item, index) => (
          <div className="hkb-win" key={`${item.payout}-${index}`}>
            <div className="hkb-win-kind">
              <Ticket size={14} />
              {item.kind}
            </div>
            <div className="hkb-win-line">
              <span>Kazanç</span>
              <strong>{money(item.payout)} <small>TRY</small></strong>
            </div>
            <div className="hkb-win-line is-muted">
              <span>Tutar</span>
              <strong>{money(item.stake)} <small>TRY</small></strong>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

// ------------------------------ Bet Checker ------------------------------ //

const BetChecker = () => {
  const [code, setCode] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const check = async (event) => {
    event.preventDefault();
    const id = code.trim();
    if (!id) return;
    setBusy(true);
    setError('');
    setResult(null);
    try {
      const { data } = await client.get(`/sports/coupon-check/${encodeURIComponent(id)}`);
      setResult(data);
    } catch (requestError) {
      setError(requestError.response?.data?.detail || 'Kupon bulunamadı');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="hkb-panel">
      <h3 className="hkb-panel-head">BET CHECKER</h3>
      <form className="hkb-panel-body" onSubmit={check}>
        <input
          className="hkb-input"
          placeholder="Bahis Kuponu ID"
          value={code}
          onChange={(changeEvent) => setCode(changeEvent.target.value)}
        />
        <button type="submit" className="hkb-btn" disabled={busy || !code.trim()}>
          {busy ? <Loader2 size={14} className="herkul-spin" /> : 'CHECK'}
        </button>

        {error && <p className="hkb-note is-error">{error}</p>}

        {result && (
          <div className="hkb-check">
            <div className="hkb-check-top">
              <span className={`hkb-status is-${result.status}`}>{result.status}</span>
              <span>{Number(result.total_odd).toFixed(2)}x</span>
            </div>
            <div className="hkb-check-line">
              <span>Tutar</span><strong>{money(result.stake)} ₺</strong>
            </div>
            <div className="hkb-check-line">
              <span>{result.payout > 0 ? 'Ödeme' : 'Muhtemel'}</span>
              <strong>{money(result.payout > 0 ? result.payout : result.possible_win)} ₺</strong>
            </div>
            <ul className="hkb-check-picks">
              {result.picks.map((pick, index) => (
                <li key={`${pick.match}-${index}`}>
                  <span>{pick.match || '—'}</span>
                  <b>{pick.selection}</b>
                </li>
              ))}
            </ul>
          </div>
        )}
      </form>
    </section>
  );
};

// ---------------------------- Bahis Oluşturucu --------------------------- //

// Referanstaki "Bahis Oluşturucu": oyuncu tutar, hedef kazanç ve oran
// aralığı verir; araç bu aralıktaki seçimlerden hedefe en yakın kuponu
// **istemcide** kurar. Sunucuya yeni bir uç eklenmedi — seçimler zaten
// vitrindeki oranlardır ve kupon her zamanki doğrulamadan geçer.
const BetBuilder = ({ sb, events }) => {
  const [amount, setAmount] = useState('');
  const [target, setTarget] = useState('');
  const [minOdd, setMinOdd] = useState(1.2);
  const [maxOdd, setMaxOdd] = useState(10);
  const [note, setNote] = useState('');

  const build = () => {
    const stake = Number(amount) || 0;
    const want = Number(target) || 0;
    if (stake <= 0 || want <= stake) {
      setNote('Tutar ve hedef kazanç girin (hedef, tutardan büyük olmalı).');
      return;
    }
    const needed = want / stake;

    // Aralığa uyan tek seçimler; her olaydan **en fazla bir** bacak alınır —
    // aynı olaya iki seçim korelasyonlu kupon olurdu ve backend zaten
    // reddeder.
    const candidates = [];
    events.forEach((event) => {
      const market = (event.markets || [])[0];
      (market?.selections || []).forEach((selection) => {
        const odd = Number(selection.odd) || 0;
        if (odd >= minOdd && odd <= maxOdd) {
          candidates.push({ event, market, selection, odd });
        }
      });
    });
    if (!candidates.length) {
      setNote('Bu oran aralığında seçim bulunamadı.');
      return;
    }

    candidates.sort((a, b) => a.odd - b.odd);
    const used = new Set();
    const chosen = [];
    let product = 1;
    // Hedefe ulaşana kadar en düşük orandan başlayarak bacak eklenir:
    // az sayıda yüksek oran yerine dengeli bir kupon çıkar.
    for (let i = candidates.length - 1; i >= 0 && product < needed; i -= 1) {
      const item = candidates[i];
      if (used.has(item.event.event_key)) continue;
      used.add(item.event.event_key);
      chosen.push(item);
      product *= item.odd;
      if (chosen.length >= 8) break;
    }

    if (!chosen.length) {
      setNote('Kupon oluşturulamadı.');
      return;
    }

    sb.clearCoupon();
    chosen.forEach((item) => sb.addBookPick(item.event, item.market, item.selection));
    sb.setStake(stake);
    setNote(
      `${chosen.length} seçim eklendi · toplam oran ${product.toFixed(2)}`
      + (product < needed ? ' (hedefin altında kaldı)' : ''),
    );
  };

  return (
    <section className="hkb-panel">
      <h3 className="hkb-panel-head">BAHİS OLUŞTURUCU</h3>
      <div className="hkb-panel-body">
        <div className="hkb-info">
          <Info size={14} />
          <div>
            <strong>Toplam Oran</strong>
            <span>Min Değer: {minOdd.toFixed(2)} · Maks Değer: {maxOdd.toFixed(2)}</span>
          </div>
        </div>

        <div className="hkb-two">
          <label>
            Bahis tutarı
            <input
              className="hkb-input"
              type="number"
              min="0"
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
              placeholder="0"
            />
          </label>
          <label>
            Tercih edilen kazanç
            <input
              className="hkb-input"
              type="number"
              min="0"
              value={target}
              onChange={(event) => setTarget(event.target.value)}
              placeholder="0"
            />
          </label>
        </div>

        <p className="hkb-label">Oran Aralığı</p>
        <div className="hkb-two">
          <label>
            Min
            <input
              className="hkb-input"
              type="number"
              step="0.1"
              min="1.01"
              value={minOdd}
              onChange={(event) => setMinOdd(Number(event.target.value) || 1.01)}
            />
          </label>
          <label>
            Maks
            <input
              className="hkb-input"
              type="number"
              step="0.1"
              min="1.02"
              value={maxOdd}
              onChange={(event) => setMaxOdd(Number(event.target.value) || 10)}
            />
          </label>
        </div>

        <button type="button" className="hkb-btn" onClick={build}>BAHİS OLUŞTUR</button>
        {note && <p className="hkb-note">{note}</p>}
      </div>
    </section>
  );
};

// -------------------------------- Sonuçlar ------------------------------- //

// Referansta "Sonuçlar" sekmesi sağlayıcının maç sonucu arşividir; bizde
// böyle bir besleme yok. Oyuncunun gerçekten aradığı bilgi kendi kuponunun
// sonucudur, bu yüzden sekme sonuçlanmış kuponları gösterir — uydurma bir
// arşiv çizmek yerine elimizdeki gerçek sonuç budur.
const ResultsPanel = ({ user }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) { setLoading(false); return undefined; }
    let alive = true;
    client.get('/finance/coupons')
      .then(({ data }) => {
        if (!alive) return;
        const list = Array.isArray(data) ? data : (data?.items || []);
        setItems(list.filter((item) => item.status && item.status !== 'active'));
      })
      .catch(() => { /* sessiz */ })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [user]);

  if (!user) {
    return (
      <section className="hkb-block">
        <div className="hkb-block-head"><h2>SONUÇLAR</h2></div>
        <p className="hkb-empty">
          Sonuçlanan kuponlarını görmek için giriş yapmalısın.
        </p>
      </section>
    );
  }

  return (
    <section className="hkb-block">
      <div className="hkb-block-head"><h2>SONUÇLAR</h2></div>
      {loading && <div className="hkb-empty"><Loader2 size={16} className="herkul-spin" /></div>}
      {!loading && items.length === 0 && (
        <p className="hkb-empty">Sonuçlanmış kuponun yok.</p>
      )}
      <div className="hkb-table">
        {items.map((coupon) => (
          <div className="hkb-result" key={coupon.id}>
            <span className={`hkb-status is-${coupon.status}`}>{coupon.status}</span>
            <span className="hkb-result-legs">{(coupon.picks || []).length} seçim</span>
            <span>{Number(coupon.total_odd || 0).toFixed(2)}x</span>
            <span>{money(coupon.stake)} ₺</span>
            <strong>{money(coupon.payout)} ₺</strong>
          </div>
        ))}
      </div>
      {items.length > 0 && (
        <Link to="/hesap/bahis-kuponlari" className="hkb-more">
          TÜM KUPONLARIM
        </Link>
      )}
    </section>
  );
};

export default HerkulSportsPage;
